import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { Badge } from "@/components/Badge";
import { InvestorTrendChart } from "@/components/InvestorTrendChart";
import { PriceChart } from "@/components/PriceChart";
import { RsiChart } from "@/components/RsiChart";
import { Section } from "@/components/Section";
import { Centered, Spinner } from "@/components/Spinner";
import { Stat } from "@/components/Stat";
import { ApiError, api } from "@/lib/api";
import {
  directionLabel,
  isoShortDate,
  marketLabel,
  money,
  num,
  sessionLabel,
  trendLabel,
} from "@/lib/format";
import { usePublishRefresh } from "@/lib/refresh";
import {
  colors,
  directionColor,
  histogramColor,
  trendColor,
} from "@/lib/theme";
import { useFetch } from "@/lib/useFetch";

export function DetailScreen() {
  const { symbol = "" } = useParams();
  const sym = symbol.toUpperCase();

  // 종목 메타(이름/시장/거래소)는 신호가 없어도 필요하므로 레지스트리에서 가져온다.
  const tickers = useFetch((s) => api.listTickers(undefined, s), []);

  // 아직 폴링에서 신호가 산출되지 않은 종목은 404 를 준다.
  // 이는 장애가 아니라 '신호 없음'이므로 화면 전체를 에러로 덮지 않는다.
  const history = useFetch(
    async (s) => {
      try {
        return await api.signalHistory(sym, 30, s);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    [sym],
    { revalidateOnFocus: true },
  );

  const rsi = useFetch((s) => api.rsiHistory(sym, 90, s), [sym], {
    revalidateOnFocus: true,
  });

  const ticker = tickers.data?.data.find((t) => t.symbol === sym) ?? null;
  const latest = history.data?.data?.[0] ?? null;
  const market = ticker?.market ?? latest?.market ?? null;
  const isKr = market === "KR";

  // 투자자별 매매동향은 국내(KR) 종목에서만 조회한다.
  const investor = useFetch(
    (s) => (isKr ? api.investorTrend(sym, 30, s) : Promise.resolve(null)),
    [sym, isKr],
  );

  const refreshAll = useCallback(() => {
    tickers.refresh();
    history.refresh();
    rsi.refresh();
    investor.refresh();
  }, [tickers.refresh, history.refresh, rsi.refresh, investor.refresh]);

  usePublishRefresh({
    refresh: refreshAll,
    refreshing:
      tickers.refreshing ||
      history.refreshing ||
      rsi.refreshing ||
      investor.refreshing,
    updatedAt: rsi.updatedAt,
  });

  if (tickers.loading || history.loading || rsi.loading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  // 레지스트리에도 없고 신호도 없으면 알 수 없는 종목이다.
  if (!ticker && !latest) {
    return (
      <Centered>
        <p className="hint">{sym} 정보를 찾을 수 없습니다.</p>
        {(tickers.error ?? history.error ?? rsi.error) && (
          <p className="error">{tickers.error ?? history.error ?? rsi.error}</p>
        )}
      </Centered>
    );
  }

  const points = rsi.data?.data ?? [];
  const lastPoint = points.length > 0 ? points[points.length - 1] : null;
  const lastRsi = [...points].reverse().find((p) => p.rsi != null)?.rsi ?? null;
  // 현재가: 신호 → 최신 스냅샷 → 마지막 종가 순으로 있는 값을 쓴다.
  const currentPrice = latest?.price ?? ticker?.price ?? lastPoint?.close ?? null;

  const name = latest?.name ?? ticker?.name ?? sym;

  return (
    <>
      <div className="detail-head">
        <div className="card-head-main">
          <div className="header-title">{name}</div>
          <p className="session">
            {market ? marketLabel(market) : "—"}
            {latest ? ` · ${sessionLabel(latest.session)} 기준` : " · 신호 미산출"}
            {ticker?.excd ? ` · ${ticker.excd}` : ""}
            {ticker && !ticker.active ? " · 해제됨" : ""}
          </p>
        </div>
        <div className="card-head-side">
          {latest ? (
            <Badge
              label={directionLabel(latest.direction)}
              color={directionColor(latest.direction)}
            />
          ) : (
            <Badge label="신호 없음" color={colors.hold} />
          )}
          <div className="price">{money(currentPrice)}</div>
        </div>
      </div>

      <Section title="지표">
        {latest ? (
          <div className="stat-grid">
            <Stat label="RSI(14)" value={num(latest.rsi, 1)} />
            <Stat
              label="추세"
              value={trendLabel(latest.trend)}
              color={trendColor(latest.trend)}
            />
            <Stat label="MACD" value={num(latest.macd, 3)} />
            <Stat label="시그널" value={num(latest.macd_signal, 3)} />
            <Stat
              label="히스토그램"
              value={num(latest.macd_histogram, 3)}
              color={histogramColor(latest.macd_histogram)}
            />
            <Stat
              label="SMA20 / 50"
              value={`${num(latest.sma_short, 1)} / ${num(latest.sma_long, 1)}`}
            />
          </div>
        ) : (
          <>
            {/* 신호가 없어도 저장된 일별 종가에서 RSI 는 재계산되어 있다. */}
            <div className="stat-grid">
              <Stat label="RSI(14)" value={num(lastRsi, 1)} />
              <Stat label="종가" value={money(lastPoint?.close)} />
            </div>
            <p className="form-hint">
              아직 산출된 신호가 없습니다 — 다음 폴링(확정 세션)에서 MACD·추세·점수가
              채워집니다. 아래 차트는 저장된 종가 이력으로 지금 바로 볼 수 있습니다.
            </p>
          </>
        )}
      </Section>

      {latest && (
        <Section
          title={`점수  ▲매수 ${latest.buy_score}  ▼매도 ${latest.sell_score}`}
        >
          {latest.reasons.length === 0 ? (
            <p className="hint">근거 없음(중립).</p>
          ) : (
            latest.reasons.map((r, i) => (
              <p className="reason" key={i}>
                · {r}
              </p>
            ))
          )}
        </Section>
      )}

      <Section title="종가 추이 (최근 90일)">
        <PriceChart points={points} />
      </Section>

      <Section title="RSI 추이 (최근 90일)">
        <RsiChart points={points} />
      </Section>

      {isKr && (
        <Section title="투자자별 매매동향 (순매수)">
          <InvestorTrendChart points={investor.data?.data ?? []} />
        </Section>
      )}

      {latest && (
        <Section title="신호 이력">
          {(history.data?.data ?? []).slice(0, 12).map((s, i) => (
            <div className="hist-row" key={`${s.created_at}-${i}`}>
              <span className="hist-date">{isoShortDate(s.created_at)}</span>
              <Badge
                label={directionLabel(s.direction)}
                color={directionColor(s.direction)}
              />
              <span className="hist-rsi">RSI {num(s.rsi, 1)}</span>
              <span className="hist-price">{money(s.price)}</span>
            </div>
          ))}
        </Section>
      )}
    </>
  );
}
